import { snapZero } from '@outpoll/utils';
import Decimal from 'decimal.js';
import { Numeric } from '@/lib/types/common';
import { calculateSharesQuantity } from '@/lib/utils/calculateSharesQuantity';

/**
 * Calculate Profit & Loss:
 *   (exitPrice − entryPrice) × quantity
 *
 * @param priceUsd
 * @param amountUsd
 * @returns           — Decimal PnL
 */
export function calculatePnl(priceUsd: Numeric, amountUsd: Numeric): Decimal {
  const sharesQuantity = calculateSharesQuantity(amountUsd, priceUsd);
  const entry = new Decimal(priceUsd);
  const exit = new Decimal(1);
  const qty = new Decimal(sharesQuantity);

  return exit.minus(entry).times(qty);
}

/**
 * Realized PnL of a futures event, as it should read in a notification: the
 * backend decimal string is kept verbatim (no float re-formatting), a signed
 * zero loses its sign, and the tone follows the displayed sign — same rule as
 * `pnlToneClass` / `snapZero` in the trade zone.
 */
export function futuresPnl(raw: string | number | null): { text: string; tone?: string } | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const value = Number(raw);
  const text = String(raw);
  if (!Number.isFinite(value) || value === 0) return { text: text.replace('-', '') };
  return {
    text,
    tone: value > 0 ? 'text-v2-green' : 'text-v2-btn-premium-red-light',
  };
}

/**
 * PnL tone by the *displayed* sign: a value that rounds to zero at `decimals` (or is
 * missing) stays neutral — same rule as the trade zone's `pnlToneClass`.
 */
export function pnlToneClass(value: number | null | undefined, decimals = 2): string {
  const snapped = snapZero(value ?? 0, decimals);
  if (snapped === 0) return 'text-v2-text-primary';
  return snapped > 0 ? 'text-v2-green' : 'text-v2-btn-premium-red-light';
}
